import React, { useRef, useEffect, useState } from 'react';
import MessageBubble from './MessageBubble';
import TypingIndicator from './TypingIndicator';
import WelcomeMessage from './WelcomeMessage';
import './ConversationArea.css';

const ConversationArea = ({
  messages = [],
  isTyping = false,
  isLoading = false,
  onSuggestionClick,
  autoScroll = true
}) => {
  const messagesEndRef = useRef(null);
  const containerRef = useRef(null);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = (behavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  };



  // Handle scroll events
  const handleScroll = React.useCallback(() => {
    if (!containerRef.current) return;

    // Check if user is near bottom of conversation
    const isNearBottom = () => {
      if (!containerRef.current) return true;

      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      const threshold = 100; // pixels from bottom
      return scrollHeight - scrollTop - clientHeight < threshold;
    };

    const nearBottom = isNearBottom();
    setShowScrollButton(!nearBottom);

    // If user scrolls up, they're manually scrolling
    if (!nearBottom) {
      setIsUserScrolling(true);
    } else {
      setIsUserScrolling(false);
    }
  }, []);

  // Auto-scroll when new messages arrive (only if user isn't manually scrolling)
  useEffect(() => {
    if (autoScroll && !isUserScrolling) {
      scrollToBottom();
    }
  }, [messages, autoScroll, isUserScrolling]);

  // Auto-scroll when typing indicator appears
  useEffect(() => {
    if (isTyping && !isUserScrolling) {
      scrollToBottom();
    }
  }, [isTyping, isUserScrolling]);

  // Reset user scrolling when they reach bottom
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const handleScrollToBottom = () => {
    setIsUserScrolling(false);
    scrollToBottom();
  };

  const enhanceMessageWithHandlers = (message) => {
    // Don't add onSuggestionClick to metadata to avoid duplicate handlers
    return message;
  };

  return (
    <div className="conversation-area">
      <div
        ref={containerRef}
        className="messages-container"
        onScroll={handleScroll}
      >
        <div className="messages-list">
          {messages.length === 0 ? (
            <WelcomeMessage
              onSuggestionClick={onSuggestionClick}
              isLoading={isLoading}
            />
          ) : (
            messages.map((message, index) => {
              const enhancedMessage = enhanceMessageWithHandlers(message);
              const showAvatar = index === 0 ||
                messages[index - 1]?.sender !== message.sender ||
                (new Date(message.timestamp) - new Date(messages[index - 1]?.timestamp)) > 300000; // 5 minutes

              return (
                <MessageBubble
                  key={message.id}
                  message={enhancedMessage}
                  showAvatar={showAvatar}
                  showTimestamp={true}
                  onSuggestionClick={onSuggestionClick}
                />
              );
            })
          )}

          {/* Typing Indicator */}
          <TypingIndicator
            isVisible={isTyping}
            botName="Yoga Assistant"
          />

          {/* Scroll anchor */}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Scroll to bottom button */}
      {showScrollButton && (
        <button
          className="scroll-to-bottom"
          onClick={handleScrollToBottom}
          aria-label="Scroll to bottom"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
      )}

      {/* Loading overlay */}
      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-spinner">
            <div className="spinner"></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConversationArea;